import React, { forwardRef } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * GlassCheckbox - Executive design system checkbox component
 *
 * A premium checkbox with glass morphism effects and smooth animations.
 *
 * @example
 * ```tsx
 * <GlassCheckbox
 *   checked={isChecked}
 *   onChange={(checked) => setIsChecked(checked)}
 *   label="Accept terms and conditions"
 * />
 * ```
 */

export interface GlassCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'size'> {
  /** Current checked state */
  checked: boolean;
  /** Callback when checked state changes */
  onChange: (checked: boolean) => void;
  /** Optional label text */
  label?: string;
  /** Optional description text */
  description?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Indeterminate state */
  indeterminate?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: {
    box: 'w-4 h-4',
    icon: 'w-2.5 h-2.5',
    label: 'text-sm',
    description: 'text-xs',
  },
  md: {
    box: 'w-5 h-5',
    icon: 'w-3 h-3',
    label: 'text-sm',
    description: 'text-xs',
  },
  lg: {
    box: 'w-6 h-6',
    icon: 'w-4 h-4',
    label: 'text-base',
    description: 'text-sm',
  },
};

export const GlassCheckbox = forwardRef<HTMLInputElement, GlassCheckboxProps>(
  (
    {
      checked,
      onChange,
      label,
      description,
      size = 'md',
      error = false,
      errorMessage,
      indeterminate = false,
      className,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `glass-checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const sizes = sizeClasses[size];

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked);
    };

    return (
      <div className={cn('relative', className)}>
        <label
          htmlFor={checkboxId}
          className={cn(
            'flex items-start gap-3 cursor-pointer group',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {/* Hidden native checkbox */}
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only peer"
            {...props}
          />

          {/* Custom checkbox box */}
          <span
            className={cn(
              sizes.box,
              'relative flex-shrink-0 rounded-md',
              'border-2 transition-all duration-200',
              // Unchecked state
              !checked && !indeterminate && [
                'bg-white/80 dark:bg-slate-800/80',
                'border-slate-300 dark:border-slate-600',
                'group-hover:border-[var(--accent-blue)] group-hover:bg-white dark:group-hover:bg-bg-tertiary',
              ],
              // Checked/indeterminate state
              (checked || indeterminate) && [
                'bg-gradient-to-br from-blue-500 to-blue-600',
                'border-transparent',
                'shadow-[var(--shadow-glow-blue)]',
              ],
              // Error state
              error && !checked && 'border-rose-500 group-hover:border-rose-600',
              // Focus state (via peer)
              'peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--accent-blue)]/40 peer-focus-visible:ring-offset-2'
            )}
          >
            {/* Checkmark icon */}
            {checked && (
              <Check
                className={cn(
                  sizes.icon,
                  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                  'text-white',
                  'animate-in zoom-in-50 duration-150'
                )}
                strokeWidth={3}
              />
            )}

            {/* Indeterminate dash */}
            {!checked && indeterminate && (
              <span
                className={cn(
                  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                  'w-2/3 h-0.5 bg-white rounded-full',
                  'animate-in zoom-in-50 duration-150'
                )}
              />
            )}
          </span>

          {/* Label and description */}
          {(label || description) && (
            <div className="flex flex-col">
              {label && (
                <span
                  className={cn(
                    sizes.label,
                    'font-medium text-foreground',
                    'select-none'
                  )}
                >
                  {label}
                </span>
              )}
              {description && (
                <span
                  className={cn(
                    sizes.description,
                    'text-muted-foreground/60 mt-0.5'
                  )}
                >
                  {description}
                </span>
              )}
            </div>
          )}
        </label>

        {/* Error message */}
        {error && errorMessage && (
          <p className="mt-1.5 text-sm text-rose-500 ml-8">{errorMessage}</p>
        )}
      </div>
    );
  }
);

GlassCheckbox.displayName = 'GlassCheckbox';
